/**
 * Sign-in screen tagline rotation.
 *
 * Each visit to the SignIn route picks one of these at random (via
 * `pickTagline()` + `useState` lazy initializer) so the page feels
 * fresh across visits without churning during a single session.
 *
 * Every tagline follows the same "By signing in you agree to ..."
 * contract framing — subverts the usual ToS language by making it a
 * commitment to self-improvement instead. Adding new ones: keep the
 * framing, keep it short, vary the angle (discipline / precision /
 * psychology / swagger).
 */

export const SIGN_IN_TAGLINES: readonly string[] = [
  // Discipline / reps
  "By signing in you agree to drill harder than yesterday.",
  "By signing in you agree to fold the hand you secretly wanted to play.",
  "By signing in you agree to study the hand you most want to forget.",
  "By signing in you agree to put in the reps your competition won't.",
  "By signing in you agree to grind theory before you grind tables.",

  // Precision / math
  "By signing in you agree to size your bets on purpose.",
  "By signing in you agree to call a leak by its name.",
  "By signing in you agree to think one street ahead.",
  "By signing in you agree to know your range better than villain knows theirs.",
  "By signing in you agree to stop guessing and start counting.",

  // Psychology / poise
  "By signing in you agree to read the player, not the hand.",
  "By signing in you agree to make decisions, not excuses.",
  "By signing in you agree to bluff with conviction or fold with grace.",

  // Edge / swagger
  "By signing in you agree to outwork the table: every seat, every street.",
  "By signing in you agree to play the math, hunt the leaks, exploit the rest.",
  "By signing in you agree to leave a smarter player at the table than the one who sat down.",
  "By signing in you agree to find an edge in every seat.",
  "By signing in you agree to take the spot nobody else wanted.",

  // Candor / self-own
  "By signing in you agree to find out your turn calls were the leak.",
  "By signing in you agree to stop blaming the river.",
  "By signing in you agree to put a number on the spew.",
  "By signing in you agree to look at the hand history you've been avoiding.",
  "By signing in you agree to learn why you overfold the turn.",
  'By signing in you agree to admit "I had a read" is not a strategy.',
  "By signing in you agree to drill the spot you keep getting wrong.",
  "By signing in you agree to accept the call was bad even though it got there.",
  'By signing in you agree to hear that "standard" was doing a lot of work in that sentence.',
  "By signing in you agree to stop slow-rolling your own study.",
] as const;

/** Random index into SIGN_IN_TAGLINES. The caller is expected to call
 *  this once per mount (via `useState(pickTagline)`) so the line stays
 *  stable across re-renders within a single visit but changes between
 *  visits. */
export function pickTagline(rand: () => number = Math.random): string {
  return SIGN_IN_TAGLINES[Math.floor(rand() * SIGN_IN_TAGLINES.length)]!;
}
